import {
  FormControl,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { ChangeableProperties } from '@/configurator/utils/shapeConfigUtils'
import { familyDrawsKey } from '@/configurator/blueprint-overlay/anchors'

// Enums for new properties
export enum ExtensionType {
  STANDART = 'STANDART',
  MECHANIC_RECLINER = 'MECHANIC_RECLINER',
  ELECTRIC_RECLINER = 'ELECTRIC_RECLINER',
}

export enum BackrestType {
  STANDART = 'STANDART',
  MECHANIC = 'MECHANIC',
  ELECTRIC = 'ELECTRIC',
}

// Configuration for dimension fields
export const dimensionFieldsConfig = [
  {
    key: 'length' as keyof ChangeableProperties,
    label: 'Length',
    measurement: 'cm',
  },
  {
    key: 'width' as keyof ChangeableProperties,
    label: 'Width',
    measurement: 'cm',
  },
  {
    key: 'height' as keyof ChangeableProperties,
    label: 'Height',
    measurement: 'cm',
  },
  {
    key: 'seat_height' as keyof ChangeableProperties,
    label: 'Seat Height',
    measurement: 'cm',
  },
  {
    key: 'seat_depth' as keyof ChangeableProperties,
    label: 'Seat Depth',
    measurement: 'cm',
  },
  {
    key: 'seat_width' as keyof ChangeableProperties,
    label: 'Seat Width',
    measurement: 'cm',
  },
  {
    key: 'armrest_width' as keyof ChangeableProperties,
    label: 'Armrest Width',
    measurement: 'cm',
  },
  {
    key: 'backrest_width' as keyof ChangeableProperties,
    label: 'Backrest Width',
    measurement: 'cm',
  },
  {
    key: 'corner_part_length' as keyof ChangeableProperties,
    label: 'Corner Part Length',
    measurement: 'cm',
  },
  {
    key: 'corner_radius' as keyof ChangeableProperties,
    label: 'Corner Radius',
    measurement: 'cm',
  },
  {
    key: 'number_of_big_pillows' as keyof ChangeableProperties,
    label: 'Number of Pillows',
    measurement: 'pcs',
  },
  {
    key: 'number_of_small_pillows' as keyof ChangeableProperties,
    label: 'Number of Small Pillows',
    measurement: 'pcs',
  },
  {
    key: 'spread_of_small_pillows' as keyof ChangeableProperties,
    label: 'Spread of Small Pillows',
    measurement: 'cm',
  },
  {
    key: 'spread_of_big_pillows' as keyof ChangeableProperties,
    label: 'Spread of Pillows',
    measurement: 'cm',
  },
  {
    key: 'size_of_big_pillow' as keyof ChangeableProperties,
    label: 'Size of Pillow',
    measurement: 'cm',
  },
  {
    key: 'size_of_pillow' as keyof ChangeableProperties,
    label: 'Size of Small Pillow',
    measurement: 'cm',
  },
  {
    key: 'angle' as keyof ChangeableProperties,
    label: 'Angle',
    measurement: '°',
  },
  {
    key: 'extendable_part_length' as keyof ChangeableProperties,
    label: 'Extendable Part Length',
    measurement: 'cm',
  },
  {
    key: 'mattress_width' as keyof ChangeableProperties,
    label: 'Mattress Width',
    measurement: 'cm',
  },
  {
    key: 'mattress_length' as keyof ChangeableProperties,
    label: 'Mattress Length',
    measurement: 'cm',
  },
  {
    key: 'seat_sections' as keyof ChangeableProperties,
    label: 'Seat Sections',
    measurement: '',
  },
  {
    key: 'backrest_sections' as keyof ChangeableProperties,
    label: 'Backrest Sections',
    measurement: '',
  },
  {
    key: 'extension_type' as keyof ChangeableProperties,
    label: 'Extension Type',
    measurement: '',
  },
  {
    key: 'backrest_type' as keyof ChangeableProperties,
    label: 'Backrest Type',
    measurement: '',
  },
  {
    key: 'covered_side' as keyof ChangeableProperties,
    label: 'Covered Side',
    measurement: '',
  },
] as const

// Edited directly on the 2D blueprint (SofaDimensionsBlueprintField), so the
// forms leave them out of the field grid.
export const ON_BLUEPRINT_KEYS = [
  'width',
  'length',
  'backrest_width',
  'armrest_width',
  'extendable_part_length',
  'corner_part_length',
] as const

export const isOnBlueprintKey = (key: string, type?: string) =>
  (ON_BLUEPRINT_KEYS as readonly string[]).includes(key) ||
  // corner_radius / angle only where a shape family draws them (ARCHCORNERL).
  (!!type && familyDrawsKey(type, key))

interface DimensionFieldItemProps {
  fieldKey: string
  measurement: string
  field: { value: any; onChange: (value: any) => void }
}

function EnumSelectItem({
  field,
  values,
  placeholder,
  fallback,
}: {
  field: DimensionFieldItemProps['field']
  values: string[]
  placeholder: string
  fallback: string
}) {
  return (
    <FormItem>
      <FormControl>
        <Select
          value={field.value || fallback}
          onValueChange={value => field.onChange(value)}
        >
          <SelectTrigger>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {values.map(type => (
              <SelectItem key={type} value={type}>
                {type.replace(/_/g, ' ')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormControl>
      <FormMessage />
    </FormItem>
  )
}

export function DimensionFieldItem({
  fieldKey,
  measurement,
  field,
}: DimensionFieldItemProps) {
  if (fieldKey === 'extension_type') {
    return (
      <EnumSelectItem
        field={field}
        values={Object.values(ExtensionType)}
        placeholder="Select extension type"
        fallback={ExtensionType.STANDART}
      />
    )
  }
  if (fieldKey === 'backrest_type') {
    return (
      <EnumSelectItem
        field={field}
        values={Object.values(BackrestType)}
        placeholder="Select backrest type"
        fallback={BackrestType.STANDART}
      />
    )
  }
  if (fieldKey === 'covered_side') {
    return (
      <FormItem className="flex flex-row items-center gap-2 py-1.5">
        <FormControl>
          <Switch
            checked={field.value || false}
            onCheckedChange={field.onChange}
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    )
  }
  return (
    <FormItem>
      <FormControl>
        <div className="relative">
          <Input
            type="number"
            placeholder="0"
            className="pr-10"
            {...field}
            value={field.value ?? ''}
            onChange={e => {
              const value = e.target.value
              field.onChange(value === '' ? null : Number(value))
            }}
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <span className="text-gray-500 text-sm">{measurement}</span>
          </div>
        </div>
      </FormControl>
      <FormMessage />
    </FormItem>
  )
}
